import Link from 'next/link'
import type { ReactNode } from 'react'

/* Material */
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Typo from '@mui/material/Typography'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

/* Components */
import StaticBreadcrumbs from 'src/components/Breadcrumbs/StaticBreadcrumbs'
import Seo from './Seo'

/* Functions */
import formatEuro from 'src/functions/formatEuro'

/* Types */
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'

/* Config */
import config from 'src/config'

/* Assets */
import DhlLogo from 'src/images/logos/thirdParty/DHL.svg'
import NachnahmeIcon from 'src/images/icons/nachnahme.webp'
import PayPalLogo from 'src/images/logos/thirdParty/PayPal.svg'
import RechnungIcon from 'src/images/icons/rechnung.webp'
import SepaLogo from 'src/images/logos/thirdParty/SEPA.svg'
import VorkasseIcon from 'src/images/icons/vorkasse.webp'

const breadcrumb: BreadcrumbItem[] = [
  { title: 'Zahlung & Versand' },
]

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(7),
      '& a': {
        color: 'inherit',
      },
    },

    intro: {
      maxWidth: 760,
      marginBottom: theme.spacing(4),
    },

    section: {
      marginTop: theme.spacing(5),
    },

    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),

      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
    },

    card: {
      display: 'grid',
      gridTemplateColumns: '64px minmax(0, 1fr)',
      gap: theme.spacing(2),
      alignItems: 'start',
      padding: theme.spacing(2.5),
      minHeight: 148,
    },

    iconBox: {
      width: 56,
      height: 56,
      borderRadius: 4,
      border: '1px solid rgba(15, 23, 42, 0.12)',
      background: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '& img': {
        maxWidth: 42,
        maxHeight: 32,
        objectFit: 'contain',
      },

      '& svg': {
        fontSize: 30,
      },
    },

    cardText: {
      color: 'rgba(0, 0, 0, .68)',
    },

    note: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2.5),
    },
  })
)

type InfoCard = {
  title: string
  body: string
  icon?: string
  fallbackIcon?: ReactNode
}

const formatShippingCost = (amount: number) =>
  amount > 0 ? formatEuro(amount) : 'kostenfrei'

const getPaymentMethods = (): InfoCard[] => [
  config.PayPalDisplay && {
    title: 'PayPal',
    body: 'Sichere Online-Zahlung über PayPal. Die Zahlungsabwicklung erfolgt direkt über PayPal.',
    icon: PayPalLogo.src,
  },
  config.PayPalDisplay && {
    title: 'Kreditkarte via PayPal',
    body: 'Kreditkartenzahlungen werden sicher über PayPal abgewickelt. Ein PayPal-Konto ist je nach PayPal-Auswahl nicht zwingend erforderlich.',
    fallbackIcon: <CreditCardIcon />,
  },
  config.PayPalDisplay && {
    title: 'SEPA-Lastschrift via PayPal',
    body: 'SEPA-Lastschrift wird über PayPal bereitgestellt und im Checkout von PayPal verarbeitet.',
    icon: SepaLogo.src,
  },
  config.VorkasseDisplay && {
    title: 'Vorkasse',
    body: 'Nach der Bestellung erhalten Sie unsere Auftragsbestätigung mit den Bankdaten für die Überweisung.',
    icon: VorkasseIcon.src,
  },
  config.NachnahmeDisplay && {
    title: 'Nachnahme',
    body: 'Sie bezahlen den Rechnungsbetrag direkt bei Lieferung bzw. Übergabe der Ware.',
    icon: NachnahmeIcon.src,
  },
  config.RechnungDisplay && {
    title: 'Auf Rechnung',
    body: 'Sie erhalten eine Rechnung mit der Auftragsbestätigung und begleichen den Betrag innerhalb der angegebenen Frist.',
    icon: RechnungIcon.src,
  },
  config.DemoPaymentDisplay && {
    title: 'Demo-Zahlung',
    body: 'Diese Zahlungsart ist nur für Testbestellungen aktiv.',
    fallbackIcon: <CreditCardIcon />,
  },
].filter(Boolean) as InfoCard[]

const shippingMethods: InfoCard[] = [
  {
    title: 'DHL-Paketversand',
    body: `Paketfähige Artikel versenden wir mit DHL. Innerhalb Deutschlands ist der Paketversand ${formatShippingCost(config.versandkosten.deutschland)}.`,
    icon: DhlLogo.src,
  },
  {
    title: 'Speditionslieferung',
    body: `Große Möbel und Sperrgut liefern wir per Spedition. Für deutschlandweite Lieferungen berechnen wir pauschal ${formatShippingCost(config.versandkosten.spedition)}.`,
    fallbackIcon: <LocalShippingOutlinedIcon />,
  },
  {
    title: 'Bestell- und Zahlungsbelege',
    body: 'Alle Angaben zur gewählten Zahlungsart, Lieferadresse und Versandart erhalten Sie in der Bestellbestätigung.',
    fallbackIcon: <ReceiptLongIcon />,
  },
]

const foreignFreightShippingMethod: InfoCard = {
  title: 'Spedition ins Ausland',
  body: 'Speditionslieferungen ins Ausland sind nur möglich, wenn für das Lieferland ein Preis hinterlegt ist. Sonst erstellen wir gerne ein Angebot auf Anfrage.',
  fallbackIcon: <Inventory2OutlinedIcon />,
}

const ZahlungUndVersand = () => {
  const classes = useStyles()
  const paymentMethods = getPaymentMethods()
  const germanyOnly = config.deliveryCountries.length === 1 && config.deliveryCountries[0] === 'DE'
  const availableShippingMethods = germanyOnly
    ? shippingMethods
    : [...shippingMethods.slice(0, 2), foreignFreightShippingMethod, ...shippingMethods.slice(2)]

  return <Container maxWidth='lg' className={classes.container}>

    <Seo />

    <StaticBreadcrumbs items={breadcrumb} />

    <Typo component='h1' variant='h4' color='inherit' gutterBottom>
      Zahlung & Versand
    </Typo>

    <Typo component='p' variant='h6' color='inherit' className={classes.intro}>
      Hier finden Sie die aktuell im Shop aktivierten Zahlungsarten sowie die wichtigsten Informationen zu DHL-Paketversand und Speditionslieferungen.
    </Typo>

    <section className={classes.section}>
      <Typo component='h2' variant='h5' color='inherit' gutterBottom>
        Zahlungsarten
      </Typo>

      <div className={classes.grid}>
        {paymentMethods.map(method =>
          <Paper key={method.title} className={classes.card} elevation={0} variant='outlined'>
            <div className={classes.iconBox}>
              {method.icon
                ? <img src={method.icon} alt="" loading='lazy' />
                : method.fallbackIcon
              }
            </div>
            <div>
              <Typo component='h3' variant='h6' color='inherit'>{method.title}</Typo>
              <Typo className={classes.cardText}>{method.body}</Typo>
            </div>
          </Paper>
        )}
      </div>
    </section>

    <section className={classes.section}>
      <Typo component='h2' variant='h5' color='inherit' gutterBottom>
        Versandarten
      </Typo>

      <div className={classes.grid}>
        {availableShippingMethods.map(method =>
          <Paper key={method.title} className={classes.card} elevation={0} variant='outlined'>
            <div className={classes.iconBox}>
              {method.icon
                ? <img src={method.icon} alt="" loading='lazy' />
                : method.fallbackIcon
              }
            </div>
            <div>
              <Typo component='h3' variant='h6' color='inherit'>{method.title}</Typo>
              <Typo className={classes.cardText}>{method.body}</Typo>
            </div>
          </Paper>
        )}
      </div>
    </section>

    {germanyOnly && <Paper className={classes.note} elevation={0} variant='outlined'>
      <Typo component='h2' variant='h5' color='inherit' gutterBottom>
        Auslandsversand
      </Typo>
      <Typo paragraph>Informationen zu Lieferungen außerhalb Deutschlands finden Sie in unserer Versandübersicht.</Typo>
      <Link href='/auslandsversandkosten' passHref legacyBehavior>
        <Button component='a' variant='outlined'>Informationen zum Auslandsversand</Button>
      </Link>
    </Paper>}

  </Container>
}

export default ZahlungUndVersand
